import type { ProjectVersion_Insert_Input } from "@/graphql";

import type { ProjectScalarInput } from "./projectScalarWrites";

// The trail row for the project row's own scalars — the third write rail's half of the base-info
// form, next to calculationVersionInput.ts's builders for the other rails.
//
// entityType is "project" and the summary must not contain the word "revision": isRevisionEvent in
// projectVersion.types.ts is `entityType === "project" && /\brevisions?\b/i.test(summary)`, so a
// stray mention would file the row under the Team chip instead of Base info.
//
// createdBy, tenantId and source are permission presets, and revisionId and versionNumber are
// trigger-stamped — setting any of them here would be rejected.

/** Resolves the two id-valued columns to the label the form showed for them. */
export interface ProjectScalarLabels {
  buildingType: (id: number) => string;
  projectStatus: (id: number) => string;
}

/**
 * `previous` is what the project's scalars were before this write, or null when there is no baseline
 * to diff against — the same shape the address and building-area rails take.
 */
export interface ProjectScalarVersionMeta {
  previous: ProjectScalarInput | null;
  labels: ProjectScalarLabels;
}

/**
 * The ten columns the write carries, in the order the form shows them, each with the value it is
 * written with.
 *
 * The list must stay level with `projectSetInput`'s columns, and that is what makes the diff below
 * non-empty by construction: `payloadChanged` gates the write on the same ten fields, so a write
 * that happens has at least one line to show.
 *
 * The first five labels are shared VERBATIM with realtimelca-api's ProjectCreationSummary, which
 * puts them on the "Project created" row — rename only in lockstep. The five under "Optional
 * details" are written from here alone, the way ModelsField is the API's alone; they are additive,
 * and the lockstep rule is about renaming.
 *
 * Both id-valued fields go through a label: the history page prints these values verbatim, and an
 * id tells a reader nothing — the same argument, and the same two names, the creation row already
 * makes ("(31) Kontorbygning", "Construction").
 */
const scalarFields = (input: ProjectScalarInput, labels: ProjectScalarLabels): { field: string; value: string | null }[] => [
  { field: "Project name", value: input.name },
  { field: "Project status", value: input.projectStatusId === null ? null : labels.projectStatus(input.projectStatusId) },
  { field: "Building type", value: labels.buildingType(input.buildingTypeId) },
  { field: "Year of construction", value: String(input.yearOfConstruction) },
  { field: "Year of commissioning", value: String(input.yearOfCommissioning) },
  { field: "Project number", value: input.projectNumber },
  { field: "Number of persons/users", value: input.numberOfPeople === null ? null : String(input.numberOfPeople) },
  { field: "Builder name", value: input.builderName },
  { field: "Client name", value: input.clientName },
  { field: "LCA Responsible Name", value: input.lcaResponsibleName },
];

/**
 * null and "" are the same absence to a reader — `renderValue` in VersionRow.vue prints both as "—"
 * — so clearing a field that was already empty is not a change worth a line. The five optional
 * columns are nullable and the five required ones are not, so a row's two sides can disagree about
 * which of the two an empty answer is without anything having moved.
 */
const same = (left: string | null, right: string | null): boolean => (left ?? "") === (right ?? "");

/**
 * The trail row for a base-information save, listing only the fields that actually MOVED, each
 * carrying the value it moved from (RTLCA-5820).
 *
 * It used to list five fields with `from: null` regardless of the save: the row for a Client name
 * edit named Project name, Project status, Building type and both years — none of which the user had
 * touched — with every PREVIOUS reading "—", and said nothing about the client name at all. The five
 * columns under "Optional details" could not appear in a history row however they were edited, and
 * two saves that each moved one of them produced two rows with byte-identical text.
 *
 * Paired by index before filtering, like the address rail: the filtered list has no index left to
 * look a baseline up by. No baseline lists every filled field with `from: null`, which is the state
 * a first write is in.
 *
 * The summary stays "Base information updated" whichever of the ten moved. Unlike the address rail,
 * which says "Project country set" when that is all it did, every one of these fields is on the
 * base-information form and there is no narrower true statement to make about a subset of them.
 */
export const buildProjectScalarVersionInput = (
  input: ProjectScalarInput,
  meta: ProjectScalarVersionMeta
): ProjectVersion_Insert_Input => {
  const next = scalarFields(input, meta.labels);
  const previous = meta.previous === null ? null : scalarFields(meta.previous, meta.labels);

  const changes = next
    .map((entry, index) => ({ entry, previousValue: previous === null ? null : previous[index].value }))
    .filter(({ entry, previousValue }) => !same(previousValue, entry.value))
    .map(({ entry, previousValue }) => ({ field: entry.field, from: previousValue, to: entry.value }));

  return {
    projectId: input.projectId,
    entityType: "project",
    operation: "update",
    summary: "Base information updated",
    entityLabel: input.name,
    changes,
  };
};
